import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Row } from '@tanstack/react-table';

import { useGetOccupancyData, useGetScenario } from '@/api/generated';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { ScenarioCards } from '@/routes/(app)/(scenarios)/components/scenario-cards';
import { ScenarioOccupancyChart } from '@/routes/(app)/(scenarios)/components/scenario-occupancy-chart';
import SimulationsTable from '@/routes/(app)/(scenarios)/components/simulations-table';
import { Simulation } from '@/routes/(app)/(scenarios)/types';

const route = getRouteApi('/app/scenarios/$scenarioId');

const ScenarioPage: FC = () => {
  const { t } = useTranslation();
  const { scenarioId } = route.useParams();
  const navigate = useNavigate();

  const { data: scenario } = useGetScenario(scenarioId, {
    query: {
      enabled: !!scenarioId,
      refetchInterval: (data) => {
        const progress = data.state.data?.progress;
        if (progress === 1) {
          return false;
        }
        return 500;
      },
    },
  });

  const processing = useMemo(() => (scenario?.progress ?? 0) < 1, [scenario]);

  // Should only fetch the occupancy data of a scenario if the scenario is finished processing.
  const { data: occupancyData } = useGetOccupancyData(scenarioId, {
    query: {
      enabled: !processing,
    },
  });
  const handleClickRow = useCallback(
    async (row: Row<Simulation>) => {
      await navigate({
        to: '/app/scenarios/$scenarioId/simulations/$simulationId',
        params: {
          scenarioId,
          simulationId: row.original.simulation_run_id,
        },
      });
    },
    [navigate, scenarioId]
  );

  if (processing || !occupancyData) {
    return (
      <Empty className='border border-dashed'>
        <EmptyHeader>
          <EmptyMedia variant='icon'>
            <Spinner />
          </EmptyMedia>
          <EmptyTitle>
            {processing
              ? t('scenario.processing.title')
              : t('scenario.occupancy.loading')}
          </EmptyTitle>
          <EmptyDescription>
            {processing
              ? t('scenario.processing.description')
              : t('scenario.occupancy.loadingDescription')}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <ScenarioCards data={occupancyData} />
      <ScenarioOccupancyChart data={occupancyData} />
      <SimulationsTable
        simulations={occupancyData.runs}
        onClickRow={handleClickRow}
      />
    </div>
  );
};

export default ScenarioPage;
